// Create a custom error type
// We do this so we can `throw new KFetchError()` below
// This results in cleaner and more consistent error handling code
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#Custom_Error_Types
class KFetchError extends Error {
  /**
   * @param  {string} url The URL being requested by the the browser/client
   * @param  {string=|object=|*=} body The response body of the failed network request
   * @param  {*=} ...params Any additional params will get passed to the parent constructor (Error)
   */
  constructor(url, body, ...params) {
    const errorMessage = `${url}\r\n${JSON.stringify(body)}`;
    super(errorMessage, ...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, KFetchError);
    }
    this.name = 'KFetchError';
    this.url = url;
    this.body = body;
  }
}

class KFetchTypeError extends Error {
  /**
   * @param  {string} url The URL being requested by the the browser/client
   * @param  {string | {} | any} options The options passed to kfetch
   * @param  {any} ...params Any additional params will get passed to the parent constructor (Error)
   */
  constructor(url, options, ...params) {
    const errorMessage = `${url}\r\n${JSON.stringify(options)}`;
    super(errorMessage, ...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, KFetchTypeError);
    }
    this.name = 'KfetchTypeError';
    this.url = url;
    this.options = options;
  }
}

/**
 * @param {string | URL} url
 * @param {object} options - https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#Supplying_request_options
 * @returns {Promise}
 */
export default async function kfetch(url, opts = {}) {
  // Add default headers since we're mostly interfacing
  // with Kareo API's - makes life a little easier
  const headers = {
    'Content-Type': 'application/json',
    ...opts.headers,
  };
  opts = {
    credentials: 'same-origin',
    ...opts,
    headers,
  };

  // Catch aborted network requests so we don't flood
  // our error tracking/logging suite (rollbar at the moment)
  let response;
  try {
    response = await fetch(url, opts);
  } catch (error) {
    if (
      (error && error.name === 'AbortError') ||
      (error && error.code === 20) ||
      (error && error.message === 'The user aborted a request.')
    ) {
      // Return a promise that never resolves
      // This _shouldn't_ cause a memory leak in modern browsers
      // https://stackoverflow.com/q/20068467
      return new Promise(() => {});
    }
    if (error instanceof TypeError) {
      throw new KFetchTypeError(url, opts, error);
    }
    throw error;
  }

  if (response) {
    // Handle Unauthorized(unauthenticated) requests
    // by redirecting to the login screen
    if (response.status === 401) {
      var currentLocation = window.location.pathname;
      if (window.location.hash) {
        currentLocation += window.location.hash;
      }
      var encodedRedirect = encodeURIComponent(currentLocation);
      if (encodedRedirect) {
        window.location.href = '/v2/#/sign-in?redirectTo=' + encodedRedirect;
      } else {
        window.location.href = '/v2/#/sign-in/';
      }
      // TODO: PM should auto login and retry failed messages;
      return new Promise(() => {});
    }

    // Get the response body (formatted as text or json)
    let body;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.startsWith('application/json')) {
        body = await response.json();
      } else {
        body = await response.text();
      }
    } catch (error) {
      // If we can't get the text() or json() it's not a big deal...
      // UNLESS, the error is an aborted request;
      // This is because FireFox handles things differently and will
      // actually crash while trying to parse .json() or .text() instead of in
      // our try/catch block around the initial `fetch` call above
      if (error.code === 20) {
        return new Promise(() => {});
      }
    }

    // Throw an error if the HTTP status code is outside the 200-299 range
    // This is useful to log on the front-end, for example;
    // We might get a 400 code if we don't `encodeURIComponent()` a user input
    // when making a request to one our API's. In which case it would be nice
    // to send this to our tracking/logging suite and then fix our code.
    if (!response.ok) {
      throw new KFetchError(url, body);
    }

    // Finally, return the response body
    return body;
  }
}
